import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';

export interface SentryUserInfo {
  userId?: string | number;
  companyId?: string | number;
}

/**
 * Module-level shared state for user info.
 * This ensures both Angular DI instance and beforeSend hook share the same data.
 */
let sharedUserInfo: SentryUserInfo = {};

@Injectable({
  providedIn: 'root'
})
export class SentryService {

  setUserInfo(info: SentryUserInfo): void {
    sharedUserInfo = { ...sharedUserInfo, ...info };
    Sentry.setTag('user_id', info.userId?.toString());
    Sentry.setTag('company_id', info.companyId?.toString());
  }

  clearUserInfo(): void {
    sharedUserInfo = {};
    Sentry.setUser(null);
  }

  getUserInfo(): SentryUserInfo {
    return sharedUserInfo;
  }

  captureMessage(message: string, level: Sentry.SeverityLevel = 'info', extra?: Record<string, any>): void {
    Sentry.captureMessage(message, {
      level,
      tags: { custom: true },
      extra,
    });
  }

  captureException(error: Error, extra?: Record<string, any>): void {
    Sentry.captureException(error, { extra });
  }

  transformEventTitle(event: Sentry.ErrorEvent): Sentry.ErrorEvent {
    const { userId, companyId } = sharedUserInfo;
    const originalTitle = event.exception?.values?.[0]?.value || event.message || 'Unknown Error';

    const userPrefix: string = (
      userId && companyId
        ? `${userId} | ${companyId}`
        : userId || companyId || 'Unknown'
    ).toString();

    let messageType: 'CUSTOM' | 'LOG' | 'ERROR';
    if (event.tags?.['custom'] === true) {
      messageType = 'CUSTOM';
    } else if (event.level === 'log' || event.logger === 'console') {
      messageType = 'LOG';
    } else {
      messageType = 'ERROR';
    }

    event.message = `${messageType}: ${userPrefix}: ${originalTitle}`;

    if (event.exception?.values?.[0]) {
      event.exception.values[0].value = event.message;
    }

    return event;
  }
}

let sentryServiceInstance: SentryService | null = null;

export const getSentryServiceInstance = (): SentryService => {
  if (!sentryServiceInstance) {
    sentryServiceInstance = new SentryService();
  }
  return sentryServiceInstance;
};

export const transformEventTitle = (event: Sentry.ErrorEvent): Sentry.ErrorEvent => {
  return getSentryServiceInstance().transformEventTitle(event);
};
